import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { Alert, Badge, Card, Spinner, cx } from '../components/ui'
import { request } from '../lib/api'
import { money, num, plural } from '../lib/format'
import type { CreditBundle, Plan } from '../lib/types'

const FAQ = [
  {
    q: 'Що вважається одним запитом?',
    a: 'Один згенерований QR-код. Пакет із 50 рядків списує 50 одиниць. Валідація (/qr/validate) і розбір (/qr/decode) — безкоштовні й не витрачають квоту.',
  },
  {
    q: 'Що станеться, коли квота закінчиться?',
    a: 'Спершу списуються передплачені кредити, якщо вони є. На платних тарифах далі вмикається тарифікація понад ліміт. На безкоштовному API поверне 402 quota_exceeded — жодних прихованих списань.',
  },
  {
    q: 'Тестові запити тарифікуються?',
    a: 'Ні. Ключ qr24_test_… проходить увесь конвеєр — валідацію, генерацію, рендер — але не списує ані квоту, ані кредити.',
  },
  {
    q: 'Чи можна змінити тариф посеред місяця?',
    a: 'Так. Оплачений період не згорає: при переході залишок днів додається до нового періоду.',
  },
  {
    q: 'Кредити мають термін дії?',
    a: 'Ні. Передплачені кредити не згорають і витрачаються лише після вичерпання місячної квоти тарифу.',
  },
]

export function Pricing() {
  const [plans, setPlans] = useState<Plan[] | null>(null)
  const [bundles, setBundles] = useState<CreditBundle[]>([])

  useEffect(() => {
    request<Plan[]>('/billing/plans', { auth: false }).then(setPlans).catch(() => setPlans([]))
    request<{ bundles: CreditBundle[] }>('/billing/credit-bundles', { auth: false })
      .then((r) => setBundles(r.bundles))
      .catch(() => undefined)
  }, [])

  return (
    <div className="container-page py-14">
      <header className="mx-auto max-w-2xl text-center">
        <h1 className="text-4xl font-bold tracking-tight">Прозорі тарифи</h1>
        <p className="mt-3 text-lg text-slate-600 dark:text-slate-400">
          Платіть за згенеровані QR-коди. Без абонплати за «місця», без прихованих комісій.
        </p>
      </header>

      {plans === null ? (
        <div className="grid place-items-center py-24">
          <Spinner className="h-8 w-8 text-brand-600" />
        </div>
      ) : (
        <div className="mt-12 grid gap-6 lg:grid-cols-4">
          {plans.map((plan) => (
            <PlanCard key={plan.code} plan={plan} highlighted={plan.code === 'starter'} />
          ))}
        </div>
      )}

      {bundles.length > 0 && (
        <section className="mt-16">
          <h2 className="text-center text-2xl font-bold tracking-tight">
            Або поповніть кредитами
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-center text-slate-600 dark:text-slate-400">
            Разова оплата без підписки. Кредити не згорають і списуються лише після
            вичерпання місячної квоти.
          </p>
          <div className="mx-auto mt-8 grid max-w-4xl gap-4 sm:grid-cols-3">
            {bundles.map((bundle) => (
              <Card key={bundle.credits} className="text-center">
                <p className="text-2xl font-bold">{num(bundle.credits)}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {plural(bundle.credits, 'кредит', 'кредити', 'кредитів')}
                </p>
                <p className="mt-4 text-xl font-semibold text-brand-600 dark:text-brand-400">
                  {money(bundle.priceUah)}
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  {money(bundle.pricePer1kUah)} за 1000
                </p>
              </Card>
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto mt-16 max-w-3xl">
        <h2 className="text-center text-2xl font-bold tracking-tight">Часті запитання</h2>
        <div className="mt-8 space-y-3">
          {FAQ.map((item) => (
            <details
              key={item.q}
              className="card group px-5 py-4 [&_summary::-webkit-details-marker]:hidden"
            >
              <summary className="flex cursor-pointer items-center justify-between gap-4 font-medium">
                {item.q}
                <svg
                  viewBox="0 0 24 24"
                  className="h-4 w-4 shrink-0 text-slate-400 transition group-open:rotate-180"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="m6 9 6 6 6-6" strokeLinecap="round" />
                </svg>
              </summary>
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">{item.a}</p>
            </details>
          ))}
        </div>
      </section>

      <Alert tone="brand" title="Потрібні особливі умови?">
        <p className="mt-1">
          On-premise, SLA 99.95%, окремий інстанс або оплата за фактом — напишіть на{' '}
          <a href="mailto:api@qr24.app" className="font-semibold underline">
            api@qr24.app
          </a>
          .
        </p>
      </Alert>
    </div>
  )
}

function PlanCard({ plan, highlighted }: { plan: Plan; highlighted: boolean }) {
  const unlimited = plan.includedRequests < 0
  const custom = plan.code === 'enterprise'
  const features = plan.features as {
    formats?: string[]
    batch?: boolean
    logo?: boolean
    hostedPages?: boolean
    history?: boolean
    webhooks?: boolean
    sla?: string
    support?: string
  }

  const rows: [string, boolean | string][] = [
    [`${unlimited ? 'Без обмежень' : num(plan.includedRequests)} запитів / міс`, true],
    [`${plan.rateLimitRps} запитів/с`, true],
    [`До ${num(plan.maxBatchSize)} QR у пакеті`, true],
    [`${plan.maxApiKeys} API-ключів`, true],
    [(features.formats ?? []).join(', ').toUpperCase(), true],
    ['Логотип у центрі', Boolean(features.logo)],
    ['Сторінки оплати за посиланням', Boolean(features.hostedPages)],
    ['Історія згенерованих QR', Boolean(features.history)],
    ['Вебхуки', Boolean(features.webhooks)],
    [features.sla ? `SLA ${features.sla}` : 'SLA', Boolean(features.sla)],
    [`Підтримка: ${features.support ?? '—'}`, true],
  ]

  return (
    <Card
      className={cx(
        'relative flex h-full flex-col',
        highlighted && 'ring-2 ring-brand-500 lg:-my-3 lg:py-3',
      )}
    >
      {highlighted && (
        <Badge tone="brand" className="absolute -top-3 left-1/2 -translate-x-1/2 shadow-sm">
          Найпопулярніший
        </Badge>
      )}

      <div>
        <h3 className="text-lg font-bold">{plan.name}</h3>
        {plan.tagline && (
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{plan.tagline}</p>
        )}
      </div>

      <div className="mt-5">
        {custom ? (
          <p className="text-3xl font-bold">Індивідуально</p>
        ) : (
          <p className="flex items-baseline gap-1.5">
            <span className="text-3xl font-bold">{money(plan.priceUah)}</span>
            {plan.priceCents > 0 && (
              <span className="text-sm text-slate-500 dark:text-slate-400">/ міс</span>
            )}
          </p>
        )}
        {plan.overageCentsPer1k > 0 && (
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            понад ліміт — {money(plan.overageCentsPer1k / 100)} за 1000
          </p>
        )}
      </div>

      <ul className="mt-6 flex-1 space-y-2.5 text-sm">
        {rows.map(([label, enabled]) => (
          <li
            key={label}
            className={cx(
              'flex items-start gap-2',
              !enabled && 'text-slate-400 line-through dark:text-slate-600',
            )}
          >
            <svg
              viewBox="0 0 24 24"
              className={cx('mt-0.5 h-4 w-4 shrink-0', enabled ? 'text-brand-600' : 'text-slate-300')}
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
            >
              <path d={enabled ? 'm5 12 5 5L20 7' : 'M6 6l12 12M18 6 6 18'} strokeLinecap="round" />
            </svg>
            <span className={enabled ? 'text-slate-600 dark:text-slate-300' : undefined}>
              {label}
            </span>
          </li>
        ))}
      </ul>

      <Link
        to={custom ? '/pricing#contact' : '/register'}
        className={cx('mt-6 w-full', highlighted ? 'btn-primary' : 'btn-secondary')}
      >
        {custom ? 'Звʼязатися' : plan.priceCents === 0 ? 'Почати безкоштовно' : 'Обрати тариф'}
      </Link>
    </Card>
  )
}
